
// Vertical bar chart for a monthly time series (collections-over-time).
// `data`: array of {month: 'YYYY-MM', count}, expected pre-sorted ascending.
// Single series — one hue (this app's primary green), no legend needed (a
// lone series is named by the section's own heading, not a legend box).
// Colours go through style, not SVG attributes: a CSS variable (the theme
// tokens) is valid in a style property and ignored in a presentation attribute.
//
// Optional, both added for the AWS costs page (2026-10-18):
//   formatValue(n)   how a value is written over its bar and in its tooltip
//                    (default: as it is) -- e.g. as money
//   reference        {value, label}: a dashed line across the chart at
//                    `value` (a monthly budget), labelled; the scale grows to
//                    keep it in view
export default function MonthlyBarChart({ data, emptyMessage, formatValue = (n) => n, reference = null }) {
  if (!data || data.length === 0) {
    return <p style={{ color: 'var(--c-text-faint-fg)', fontSize: 13, margin: '8px 0' }}>{emptyMessage}</p>;
  }
  const line = reference && Number.isFinite(reference.value) && reference.value > 0 ? reference : null;
  const max = Math.max(...data.map(d => d.count), line ? line.value : 0, 1);
  const barWidth = 28;
  const gap = 10;
  const chartHeight = 120;
  const width = data.length * (barWidth + gap);
  const lineY = line ? chartHeight - (line.value / max) * (chartHeight - 16) : null;

  return (
    <svg width="100%" viewBox={`0 0 ${width} ${chartHeight + 30}`} style={{ display: 'block', maxWidth: width }}>
      <line x1={0} y1={chartHeight} x2={width} y2={chartHeight} style={{ stroke: 'var(--c-border-strong-4)' }} strokeWidth={1} />
      {data.map((d, i) => {
        const barHeight = d.count > 0 ? Math.max((d.count / max) * (chartHeight - 16), 3) : 0;
        const x = i * (barWidth + gap);
        const [year, month] = d.month.split('-');
        return (
          <g key={d.month}>
            <title>{`${d.month}: ${formatValue(d.count)}`}</title>
            <rect x={x} y={chartHeight - barHeight} width={barWidth} height={barHeight} rx={4} style={{ fill: 'var(--c-primary)' }} />
            {d.count > 0 && (
              <text x={x + barWidth / 2} y={chartHeight - barHeight - 4} textAnchor="middle" fontSize="10" style={{ fill: 'var(--c-text-3)' }}>{formatValue(d.count)}</text>
            )}
            <text x={x + barWidth / 2} y={chartHeight + 16} textAnchor="middle" fontSize="9" style={{ fill: 'var(--c-text-faint-2)' }}>{month}/{year.slice(2)}</text>
          </g>
        );
      })}
      {line && (
        <g data-testid="chart-reference">
          <title>{line.label}</title>
          <line x1={0} y1={lineY} x2={width} y2={lineY} strokeWidth={1} strokeDasharray="4 3"
            style={{ stroke: 'var(--c-danger)' }} />
          <text x={width - 2} y={lineY - 3} textAnchor="end" fontSize="9" style={{ fill: 'var(--c-danger-fg)' }}>{line.label}</text>
        </g>
      )}
    </svg>
  );
}
